import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function SaleFAQ() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const {
    t,
    language
  } = useLanguage();
  const getFaqItems = () => {
    if (language === 'sk') {
      return [{
        question: 'Čo je to bezplatný návrh?',
        answer: 'Je to predbežná verzia vašej webstránky pred platbou.'
      }, {
        question: 'Kedy platím 15 € mesačne?',
        answer: 'Iba vtedy, ak vám návrh vyhovuje a chcete webstránku spustiť. Platíte predplatné za používanie webstránky.'
      }, {
        question: 'Môžem požiadať o zmeny?',
        answer: 'Áno, pred spustením môžeme vykonať zmeny.'
      }, {
        question: 'Čo dostanem po zaplatení?',
        answer: 'Hotovú webstránku pre vaše podnikanie, ktorá funguje a prináša klientov.'
      }, {
        question: 'Pracujete celosvetovo?',
        answer: 'Áno, pracujeme online s klientmi z rôznych krajín.'
      }];
    } else if (language === 'ru') {
      return [{
        question: 'Что такое бесплатный прототип?',
        answer: 'Это предварительный вариант вашего сайта до оплаты.'
      }, {
        question: 'Когда я плачу €15 в месяц?',
        answer: 'Только если прототип вам подходит и вы хотите запустить сайт. Вы оплачиваете подписку за использование сайта.'
      }, {
        question: 'Можно ли попросить правки?',
        answer: 'Да, мы можем внести изменения перед запуском.'
      }, {
        question: 'Что я получаю после оплаты?',
        answer: 'Готовый сайт для вашего бизнеса, который работает и приносит клиентов.'
      }, {
        question: 'Вы работаете по всему миру?',
        answer: 'Да, работаем онлайн с клиентами из разных стран.'
      }];
    } else if (language === 'ua') {
      return [{
        question: 'Що таке безкоштовний прототип?',
        answer: 'Це попередній варіант вашого сайту до оплати.'
      }, {
        question: 'Коли я сплачую €15 на місяць?',
        answer: 'Тільки якщо прототип вам підходить і ви хочете запустити сайт. Ви оплачуєте підписку за використання сайту.'
      }, {
        question: 'Чи можна попросити правки?',
        answer: 'Так, ми можемо внести зміни перед запуском.'
      }, {
        question: 'Що я отримаю після оплати?',
        answer: 'Готовий сайт для вашого бізнесу, який працює і приносить клієнтів.'
      }, {
        question: 'Ви працюєте по всьому світу?',
        answer: 'Так, працюємо онлайн з клієнтами з різних країн.'
      }];
    } else {
      return [{
        question: 'What is a free prototype?',
        answer: "It's a preliminary version of your website before payment."
      }, {
        question: 'When do I pay €15/month?',
        answer: 'Only if the prototype suits you and you want to launch the website. You pay the subscription for website usage.'
      }, {
        question: 'Can I request changes?',
        answer: 'Yes, we can make changes before launch.'
      }, {
        question: 'What do I get after payment?',
        answer: 'A ready-to-use website for your business that works and brings clients.'
      }, {
        question: 'Do you work worldwide?',
        answer: 'Yes, we work online with clients from different countries.'
      }];
    }
  };
  const faqItems = getFaqItems();
  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section id="faq" ref={ref} className="relative w-full overflow-hidden px-4 py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-249">
      <div className="relative z-10 mx-auto max-w-3xl" data-id="element-250">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-12 text-center" data-id="element-251">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
          color: 'var(--text-primary)'
        }} data-id="element-252">
            {t('faq.title')} {t('faq.titleHighlight')}
          </h2>
          <p style={{
          color: 'var(--text-secondary)'
        }} data-id="element-253">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        <div className="space-y-4" data-id="element-254">
          {faqItems.map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} data-id="element-255">
              <div className="rounded-2xl border overflow-hidden" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-256">
                <button onClick={() => toggleItem(index)} className="flex w-full items-center justify-between p-6 text-left transition-colors" aria-expanded={openIndex === index} data-id="element-257">
                  <h3 className="pr-4 font-semibold text-base" style={{
                color: 'var(--text-primary)'
              }} data-id="element-258">
                    {item.question}
                  </h3>
                  <motion.div animate={{
                rotate: openIndex === index ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className="flex-shrink-0" data-id="element-259">
                    <ChevronDownIcon className="h-5 w-5" style={{
                  color: 'var(--accent)'
                }} data-id="element-260" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-261">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-262">
                      <div className="border-t px-6 pb-6 pt-4" style={{
                  borderColor: 'var(--border)'
                }} data-id="element-263">
                        <p className="leading-relaxed" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-264">
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}